import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EsVersion = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='política-de-privacidad-aplicable-al-usuario-final'>
        Política de privacidad aplicable al usuario final
      </h4>
      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />
      <p>Fecha de entrada en vigencia: 22 de febrero de 2024</p>
      <p>
        La privacidad y la seguridad son muy importantes para nosotros en Plaid.
        La finalidad de esta Política de privacidad aplicable al usuario final
        (la “Política”) es ayudarlo a usted (el “usuario final”) a comprender de
        qué manera nosotros, en Plaid, recabamos, usamos y compartimos sus datos
        cuando usted usa los productos o servicios de Plaid; por ejemplo, cuando
        usa Plaid Portal o cuando utiliza a Plaid para conectarse y compartir
        sus datos para accionar las aplicaciones (“aplicaciones”) que usa. Estas
        aplicaciones, que ofrecen muchos servicios y productos al consumidor,
        como, por ejemplo, ayudarlo a ahorrar para su jubilación, administrar
        sus gastos, agilizar las solicitudes de crédito o transferir dinero, son
        creadas y proporcionadas por nuestros clientes comerciales (en lo
        sucesivo, “desarrolladores”), y son proporcionadas por Plaid.
      </p>
      <p>
        Esta Política se aplica a Plaid Inc., Plaid Financial Ltd. y a Plaid, B.
        V. (en forma conjunta, “Plaid”, “nosotros”, “nuestro” y “nos”). (Le
        solicitamos que consulte la sección “Cómo ponerse en contacto con Plaid”
        más adelante para conocer cuáles de estas entidades es responsable del
        tratamiento de sus datos). Esta Política no contempla los servicios que
        ofrece Plaid Consumer Reporting Agency, Inc. (“Plaid CRA”); para obtener
        más información sobre dicha política, consulte la{' '}
        <Link href='/plaid-check-consumer-report/privacy-policy/'>
          <a>Política de privacidad de CRA</a>
        </Link>
        .
      </p>
      <p>
        Debe leer esta Política detenidamente, dado que contiene información
        importante sobre sus derechos y opciones de privacidad.
      </p>

      <p>
        <em>Ir a la sección:</em>
      </p>

      <p>
        <Link href='#nuestras-prácticas-relativas-a-datos'>
          <a data-smooth-scroll data-offset='80'>
            Nuestras prácticas relativas a datos
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#datos-que-recabamos-y-categorías-de-fuentes'>
              <a data-smooth-scroll data-offset='80'>
                Datos que recabamos y categorías de fuentes
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#como-usamos-sus-datos'>
              <a data-smooth-scroll data-offset='80'>
                Cómo usamos sus datos
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#nuestras-bases-legitimadoras-para-el-tratamiento-usuarios-finales-del-eee-y-del-reino-unido'>
              <a data-smooth-scroll data-offset='80'>
                Nuestras bases legitimadoras para el tratamiento (usuarios
                finales del EEE y del Reino Unido)
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cómo-compartimos-sus-datos'>
              <a data-smooth-scroll data-offset='80'>
                Cómo compartimos sus datos
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#nuestras-prácticas-de-conservación-y-eliminación'>
              <a data-smooth-scroll data-offset='80'>
                Nuestras prácticas de conservación y eliminación
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#protección-de-datos'>
              <a data-smooth-scroll data-offset='80'>
                Protección de datos
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#algunos-detalles-finales'>
          <a data-smooth-scroll data-offset='80'>
            Algunos detalles finales...
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#transferencias-internacionales-de-datos'>
              <a data-smooth-scroll data-offset='80'>
                Transferencias internacionales de datos
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#sus-derechos-en-materia-de-protección-de-datos'>
              <a data-smooth-scroll data-offset='80'>
                Sus derechos en materia de protección de datos
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#sus-controles-de-privacidad-adicionales'>
              <a data-smooth-scroll data-offset='80'>
                Sus controles de privacidad adicionales
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cambios-en-esta-política'>
              <a data-smooth-scroll data-offset='80'>
                Cambios en esta Política
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cómo-ponerse-en-contacto-con-plaid'>
              <a data-smooth-scroll data-offset='80'>
                Cómo ponerse en contacto con Plaid
              </a>
            </Link>
          </p>
        </li>
      </ul>
      <p>
        <Link href='#resúmenes-de-actividades-de-tratamiento'>
          <a data-smooth-scroll data-offset='80'>
            Resúmenes de actividades de tratamiento
          </a>
        </Link>
      </p>
      <ul>
        <li>
          <p>
            <Link href='#resúmen-de-actividades-de-tratamiento-por-categoría-de-datos'>
              <a data-smooth-scroll data-offset='80'>
                Resúmen de actividades de tratamiento por categoría de datos
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#resúmen-de-las-actividades-de-tratamiento-por-producto'>
              <a data-smooth-scroll data-offset='80'>
                Resumen de las actividades de tratamiento por producto
              </a>
            </Link>
          </p>
        </li>
      </ul>
      <p>
        <Link href='#aviso-de-privacidad-para-el-consumidor'>
          <a data-smooth-scroll data-offset='80'>
            Aviso de privacidad para el consumidor
          </a>
        </Link>
      </p>

      <h5 id='primero-un-poco-de-contexto'>Primero, un poco de contexto</h5>
      <p>
        <em>Breve comentario sobre Plaid</em>
      </p>
      <p>
        Nuestra misión en Plaid es desbloquear la libertad financiera para todo
        el mundo. Al proporcionar acceso a datos útiles y de gran calidad sobre
        cuentas financieras, los cuales hemos traducido y estandarizado,
        permitimos que los desarrolladores de aplicaciones y servicios
        financieros puedan centrarse en crear experiencias que lo beneficien a
        usted.
      </p>
      <p>
        <em>Sobre esta Política</em>
      </p>
      <p>
        Nuestro objetivo con esta Política es ofrecer una explicación simple y
        sencilla de los datos que Plaid recaba de usted y sobre usted, y acerca
        de cómo usamos y compartimos esa información. Valoramos la transparencia
        y queremos ofrecerle una descripción clara y concisa de cómo tratamos
        sus datos.
      </p>
      <p>
        Esta Política no abarca lo que los desarrolladores de las aplicaciones
        que usted utiliza hacen con sus datos. Deberá revisar las políticas de
        privacidad o los términos de servicio para esas aplicaciones si desea
        obtener información sobre sus prácticas. Esta Política tampoco cubre los
        datos que recabamos mediante nuestros sitios web ni cuando usted
        interactúa con Plaid fuera del uso de nuestros productos o servicios,
        como, por ejemplo cuando envía correos electrónicos a Plaid
        directamente. Le recomendamos que consulte nuestra{' '}
        <Link href='#declaración-de-privacidad'>
          <a>Declaración de privacidad para todos los públicos</a>
        </Link>{' '}
        y la{' '}
        <Link href='#política-de-cookies'>
          <a>Política de cookies</a>
        </Link>{' '}
        si desea obtener más información.
      </p>
      <p>
        Los servicios de Plaid no están previstos para personas menores de 18
        años y no recabamos deliberadamente datos relacionados con menores de
        edad.
      </p>

      <h5 id='nuestras-prácticas-relativas-a-datos'>
        Nuestras prácticas relativas a datos
      </h5>
      <p>
        Plaid ha asumido el compromiso de proporcionar a los usuarios finales un
        control significativo sobre sus datos. En esta sección, se describen las
        prácticas de Plaid en relación con el tratamiento de los datos relativos
        a usted. También brindamos resúmenes de nuestras prácticas, organizadas
        en función de la categoría de los datos recabados y por producto, al
        final de esta Política en la sección denominada{' '}
        <Link href='#resúmenes-de-actividades-de-tratamiento'>
          <a>Resúmenes de actividades de tratamiento</a>
        </Link>
        .
      </p>

      <h5 id='datos-que-recabamos-y-categorías-de-fuentes'>
        Datos que recabamos y categorías de fuentes
      </h5>
      <p>
        Tal como lo explicamos con más detalle más adelante, los datos que
        recabamos, usamos y compartimos dependen de los productos y servicios de
        Plaid que usted usa o que usa la aplicación a la que está conectado. En
        función de los productos o servicios de Plaid que usted use o que use el
        desarrollador al que está conectado, Plaid podrá recabar lo siguiente:
      </p>
      <ul>
        <li>
          <p>datos que usted nos proporciona;</p>
        </li>
        <li>
          <p>
            datos provenientes de las instituciones financieras cuando usted se
            conecta a su cuenta financiera;
          </p>
        </li>
        <li>
          <p>
            datos provenientes del dispositivo electrónico que utiliza para
            conectarse a su cuenta financiera a través de Plaid o para
            interactuar de otro modo con Plaid;
          </p>
        </li>
        <li>
          <p>
            datos del desarrollador de la aplicación a la que está conectado;
          </p>
        </li>
        <li>
          <p>
            datos provenientes de nuestras empresas afiliadas, para brindarle
            soporte o para brindarle una mejor experiencia de usuario; o
          </p>
        </li>
        <li>
          <p>
            datos de otras fuentes, incluidos los proveedores de servicios y los
            servicios de verificación de identidad y de prevención de fraude.
          </p>
        </li>
      </ul>
      <p>
        <strong>Datos que usted nos proporciona.</strong> Cuando usa los
        productos o servicios de Plaid (por ejemplo, cuando utiliza Plaid para
        conectar sus cuentas financieras a una aplicación), recabamos los
        siguientes datos provenientes de usted, según sean necesarios para
        potenciar su aplicación o servicio en particular:
      </p>
      <ul>
        <li>
          <p>
            <strong>identificadores,</strong> como nombre, dirección de correo
            electrónico, fecha de nacimiento, número de Seguro Social y número
            de teléfono;
          </p>
        </li>
        <li>
          <p>
            <strong>datos de acceso </strong>solicitados por el proveedor de su
            cuenta, como su nombre de usuario y contraseña y número de ruta
            (routing number), o un token de seguridad;
          </p>
        </li>
        <li>
          <p>
            <strong>
              cualquier información adicional necesaria para conectar sus
              cuentas
            </strong>
            , incluidas preguntas y respuestas de seguridad, y contraseña de un
            solo uso (One-Time Password, OTP) o
          </p>
        </li>
        <li>
          <p>
            información a partir de{' '}
            <strong>documentos o resúmenes de cuenta</strong> que proporcione a
            Plaid, como resúmenes bancarios o recibos de sueldo.
          </p>
        </li>
      </ul>
      <p>
        Al facilitar datos de inicio de sesión e información adicional necesaria
        para conectar sus cuentas, usted también autoriza a Plaid a actuar en su
        nombre para acceder y transmitir datos desde la institución financiera
        correspondiente y hacia esta.
      </p>

      <p>
        <strong>
          Datos que recabamos de las instituciones financieras sobre sus cuentas
          y desde dichas cuentas.{' '}
        </strong>
        En función de los productos o servicios de Plaid que usted o el
        desarrollador de su aplicación utilicen, como también de la información
        que se facilite y de la forma en que se haga, podremos recabar los
        siguientes datos a partir de sus instituciones financieras:
      </p>
      <ul>
        <li>
          <p>
            <strong>datos sobre la cuenta, </strong>como nombre de la
            institución financiera; nombre, tipo y titularidad de la cuenta;
            número de sucursal, IBAN, BIC; número de cuenta, número de ruta
            (routing number) y código de entidad (sort code);
          </p>
        </li>
        <li>
          <p>
            <strong>datos sobre el saldo de la cuenta, </strong>como saldo
            actual y disponible;
          </p>
        </li>
        <li>
          <p>
            <strong>datos sobre cuentas de crédito, </strong>como fechas de
            vencimiento, saldos adeudados, cantidades y fechas de pago,
            historial de transacciones, límite de crédito, estado de reembolso y
            tasa de interés;
          </p>
        </li>
        <li>
          <p>
            <strong>datos sobre cuentas de préstamo, </strong>como fechas de
            vencimiento, estado de reembolsos, saldos, cantidades y fechas de
            pago, tasa de interés, garante, tipo de préstamo, plan de pagos y
            plazos;
          </p>
        </li>
        <li>
          <p>
            <strong>datos sobre cuentas de inversión, </strong>como información
            sobre transacciones, tipo de activo, detalles identificativos acerca
            del activo, cantidad, precio, comisiones y costo base;
          </p>
        </li>
        <li>
          <p>
            <strong>identificadores </strong>y datos sobre el titular o
            titulares de la cuenta, como nombre, dirección de correo
            electrónico, número de teléfono, fecha de nacimiento e información
            del domicilio;
          </p>
        </li>
        <li>
          <p>
            <strong>datos sobre transacciones de cuentas, </strong>como importe,
            fecha, beneficiario, tipo, cantidad, precio, ubicación, valores
            comprendidos y una descripción de la transacción; y/o
          </p>
        </li>
        <li>
          <p>
            <strong>
              datos sobre su nómina de empleados y documentos fiscales,{' '}
            </strong>
            como datos acerca de su como datos acerca de sus ingresos y su
            empleador, en casos en los que usted haya conectado sus cuentas de
            nómina o nos haya proporcionado información sobre su recibo de
            sueldo o formulario de impuestos.
          </p>
        </li>
      </ul>
      <p>
        En función del servicio de Plaid que usted o el desarrollador de su
        aplicación utilicen y la manera en que los datos estén disponibles, los
        datos recabados de sus cuentas financieras pueden incluir información de
        todas las cuentas (por ejemplo, cuenta corriente, caja de ahorro,
        tarjeta de crédito y cuentas conjuntas) accesible a través de un único
        conjunto de credenciales de cuenta. Para obtener más detalles acerca de
        los datos recabados en relación con los diferentes productos y
        servicios, consulte el{' '}
        <Link href='#resúmen-de-las-actividades-de-tratamiento-por-producto'>
          <a>Resumen de las actividades de tratamiento por producto</a>
        </Link>
      </p>
      <p>
        <strong>Datos que recibimos desde sus dispositivos. </strong> Cuando usa
        un dispositivo, como su teléfono celular, tableta o computadora, para
        conectarse a nuestros servicios por medio de una aplicación del
        desarrollador, recibimos datos sobre ese dispositivo, que incluyen lo
        siguiente:
      </p>
      <ul>
        <li>
          <p>dirección de protocolo de Internet (IP);</p>
        </li>
        <li>
          <p>
            configuración de zona horaria y ubicación, ubicación del
            dispositivo;
          </p>
        </li>
        <li>
          <p>modelo de hardware y sistema operativo; </p>
        </li>
        <li>
          <p>funciones dentro de nuestros servicios a las que accede; </p>
        </li>
        <li>
          <p>datos sobre el navegador; </p>
        </li>
        <li>
          <p>datos sobre la red; y </p>
        </li>
        <li>
          <p>
            otros datos técnicos sobre el dispositivo (como configuración y
            preferencias).{' '}
          </p>
        </li>
      </ul>
      <p>
        <strong>
          Datos que recibimos sobre usted desde los desarrolladores de
          aplicaciones proporcionadas por Plaid.{' '}
        </strong>
        Cuando Plaid deba brindar un servicio (por ejemplo, verificar su
        identidad, brindarle protección contra fraudes o riesgos, o permitirle
        realizar una transferencia bancaria), los desarrolladores de las
        aplicaciones que usted usa podrán proporcionarnos identificadores e
        información comercial sobre usted, como, su nombre, número de Seguro
        Social, dirección de correo electrónico, número de teléfono o
        información sobre sus cuentas y transacciones financieras.
      </p>
      <p>
        <strong>
          Datos provenientes de nuestras empresas afiliadas, para brindarle
          soporte o para brindarle una mejor experiencia de usuario.{' '}
        </strong>
        Podremos recabar datos provenientes de nuestras empresas afiliadas
        (empresas que se relacionan con nosotros por tener titularidad o control
        compartidos) para brindarle soporte o para mejorar su experiencia. Por
        ejemplo, si elige que Plaid CRA recuerde las cuentas financieras
        asociadas a su número de teléfono, podremos recabar los siguientes datos
        provenientes de Plaid CRA, para permitir que pueda conectarse a sus
        cuentas más rápido cuando utilice nuestros servicios:
      </p>
      <ul>
        <li>
          <p>
            <strong>identificadores, </strong>
            como nombre, dirección de correo electrónico y número de teléfono;
          </p>
        </li>
        <li>
          <p>
            <strong>datos de acceso </strong> solicitados por el proveedor de su
            cuenta, como su nombre de usuario y contraseña, nombre del banco,
            número de cuenta y de ruta (routing number) o un token de seguridad;
          </p>
        </li>
        <li>
          <p>
            cuando fuera necesario,
            <strong>
              {' '}
              datos que ayuden a verificar su identidad y a conectar sus
              cuentas,{' '}
            </strong>
            incluida su fecha de nacimiento, preguntas y respuestas de
            seguridad, y una OTP; o
          </p>
        </li>
        <li>
          <p>
            datos provenientes de su dispositivo (como se explicó con más
            detalle anteriormente).
          </p>
        </li>
      </ul>
      <p>
        <strong>Datos que recibimos sobre usted de otras fuentes.</strong>{' '}
        Cuando debamos prestar un servicio, identificar su dispositivo o ayudar
        a prevenir un fraude, abuso o amenazas contra la seguridad, también
        podremos recibir datos sobre usted directamente de terceros, incluidos
        nuestro operador de servicios inalámbricos, agentes o nuestros
        proveedores de servicios.
      </p>
      <p>
        <strong>Información que extraemos de los datos que recabamos.</strong>{' '}
        Podremos extraer información adicional sobre usted a partir de los datos
        que recabamos. Por ejemplo, podemos deducir su ubicación geográfica, sus
        ingresos anuales o el tipo de cuenta o subcuenta que ha elegido
        conectar; como cuando conecta sus cuentas de préstamos, para que podamos
        informar al desarrollador si la cuenta es para una hipoteca, un préstamo
        estudiantil o una tarjeta de crédito.
      </p>

      <h5 id='como-usamos-sus-datos'>Cómo usamos sus datos</h5>
      <p>
        Usamos sus datos con los siguientes fines empresariales y comerciales:
      </p>
      <ul>
        <li>
          <p>
            <strong>Prestar servicios:</strong> para poner en funcionamiento,
            prestar y mantener nuestros servicios.
          </p>
        </li>
        <li>
          <p>
            <strong>Desarrollar servicios existentes:</strong> para mejorar,
            perfeccionar, modificar, ampliar y desarrollar en mayor profundidad
            nuestros servicios.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Ayudar a prevenir fraudes, verificar su identidad o proteger su
              privacidad:
            </strong>{' '}
            para protegerlo a usted, a los desarrolladores, a nuestros socios, a
            Plaid y a otros contra el fraude, actividades maliciosas y otros
            riesgos relacionados con la privacidad y la seguridad.
          </p>
        </li>
        <li>
          <p>
            <strong>Desarrollar nuevos servicios:</strong> para desarrollar
            nuevos productos y servicios.
          </p>
        </li>
        <li>
          <p>
            <strong>Desarrollar conocimientos:</strong> para desarrollar
            conocimientos basados en los datos que hayamos recabado sobre usted.
            Esto incluye los datos de sus transacciones, otros datos financieros
            y datos provenientes de otras fuentes, para ayudar a que los
            desarrolladores de sus aplicaciones conectadas presten servicios o
            que usted tenga una mejor experiencia como usuario, como, por
            ejemplo, brindar un acceso más rápido a sus fondos, ayudarlo a
            detectar y prevenir posibles actividades fraudulentas, o
            personalizar los servicios que le brindan.
          </p>
        </li>
        <li>
          <p>
            <strong>Brindar soporte: </strong> para brindarles soporte a usted o
            a los desarrolladores, incluso para ayudar a responder las consultas
            que usted tenga relacionadas con nuestros servicios o las
            aplicaciones de los desarrolladores.
          </p>
        </li>
        <li>
          <p>
            <strong>Comunicarnos con usted: </strong>para comunicarnos con usted
            y enviarle, por ejemplo, avisos técnicos, actualizaciones, alertas
            de seguridad y mensajes si utiliza Plaid Portal o la función
            Remember Me.
          </p>
        </li>
        <li>
          <p>
            <strong>Investigar el uso indebido y la conducta indebida:</strong>{' '}
            para investigar el uso indebido de nuestro servicio o de las
            aplicaciones de los desarrolladores, incluida cualquier violación de
            nuestra{' '}
            <Link href='#política-de-desarrolladores'>
              <a>Política de desarrolladores</a>
            </Link>
            , actividades delictivas u otro acceso no autorizado a nuestros
            servicios.
          </p>
        </li>
        <li>
          <p>
            <strong>Con fines legales: </strong> para cumplir con las
            obligaciones contractuales y legales conforme a las leyes
            aplicables, y con otros fines legales, tales como interponer
            demandas y defenderse contra ellas.
          </p>
        </li>
        <li>
          <p>
            <strong>Con su consentimiento:</strong> para otros fines notificados
            con su consentimiento o según sus instrucciones.
          </p>
        </li>
      </ul>
      <p>
        Recabamos, utilizamos y compartimos datos globales o anonimizados de
        manera que no lo identifiquen personalmente, para cualquier fin
        permitido conforme a la ley aplicable. Esto incluye la creación o el uso
        de datos globales o anonimizados, a fin de desarrollar nuevos servicios,
        o para facilitar investigaciones, y con fines analíticos para ayudar a
        evaluar la velocidad, la precisión o la seguridad de nuestros servicios.{' '}
      </p>

      <h5 id='nuestras-bases-legitimadoras-para-el-tratamiento-usuarios-finales-del-eee-y-del-reino-unido'>
        Nuestras bases legitimadoras para el tratamiento (usuarios finales del
        EEE y del Reino Unido)
      </h5>
      <p>
        En el caso de las personas que se encuentran en el Espacio Económico
        Europeo (“EEE”) o en el Reino Unido (“RU”), Plaid únicamente trata sus
        datos personales cuando tenemos una base legitimadora válida para
        hacerlo. Nuestra base legitimadora para tratar los datos que recabamos
        dependerá del tipo de datos que recabemos y del fin para el que los
        tratamos. En general, solamente recabaremos y trataremos sus datos
        cuando:
      </p>
      <ul>
        <li>
          <p>
            estemos obligados por cualquier contrato o acuerdo con usted (por
            ejemplo, para cumplir con nuestros acuerdos de servicios del usuario
            final) y debamos cumplir con diversos requisitos legales al ofrecer
            servicios regulados, incluidos nuestros servicios de información de
            cuentas y pagos;
          </p>
        </li>
        <li>
          <p>
            necesitemos sus datos a fin de cumplir con nuestras obligaciones
            legales en virtud de la ley aplicable, para proteger los derechos
            legales de Plaid, y para prevenir e identificar actividades
            delictivas, como el lavado de dinero y el fraude. A tales efectos,
            Plaid puede considerar que es necesario compartir sus datos
            personales con entidades como tribunales, fuerzas de seguridad y
            proveedores de servicios que luchan contra el lavado de dinero;
          </p>
        </li>
        <li>
          <p>
            el tratamiento sea necesario para nuestros intereses legítimos, a
            fin de mantener la integridad de nuestros servicios de manera
            efectiva. Esto incluye recuperar información de la cuenta o prestar
            servicios de iniciación de pagos (así como verificar el resultado de
            esta actividad), interactuar con usted y garantizar que Plaid, en
            calidad de entidad regulada, cumpla con las normas previstas
            (incluidas las estipuladas por la autoridad de conductas financieras
            [Financial Conduct Authority] con respecto a Plaid Financial Ltd y
            por De Nederlandsche Bank con respecto a Plaid B. V.); o
          </p>
        </li>
        <li>
          <p>usted haya dado su consentimiento para hacerlo.</p>
        </li>
      </ul>
      <p>
        En la medida en que nos basemos en el consentimiento para recabar y
        tratar sus datos, usted tiene derecho a retirar su consentimiento en
        cualquier momento, conforme a las instrucciones proporcionadas en esta
        Política.
      </p>

      <h5 id='cómo-compartimos-sus-datos'>Cómo compartimos sus datos</h5>
      <p>Compartimos sus datos por las siguientes razones:</p>
      <ul>
        <li>
          <p>
            con el desarrollador de la aplicación que usted está utilizando y
            según las indicaciones de dicho desarrollador;
          </p>
        </li>
        <li>
          <p>para el cumplimiento de cualquier contrato con usted;</p>
        </li>
        <li>
          <p>
            con nuestros encargados del tratamiento de datos y otros proveedores
            de servicios, socios o contratistas en relación con los servicios
            que nos prestan a nosotros o a los desarrolladores;
          </p>
        </li>
        <li>
          <p>
            con instituciones financieras, para ayudar a establecer, mantener y
            administrar una conexión que usted haya elegido hacer entre las
            cuentas de su institución financiera y una aplicación;
          </p>
        </li>
        <li>
          <p>
            cuando consideremos de buena fe que la divulgación es apropiada para
            cumplir con la normativa y legislación aplicables o con un proceso
            legal (por ejemplo, una orden o citación judicial);
          </p>
        </li>
        <li>
          <p>
            en relación con un cambio de titularidad o control de la totalidad o
            parte de nuestra empresa (por ejemplo, una fusión, adquisición,
            reestructuración o quiebra);
          </p>
        </li>
        <li>
          <p>
            entre Plaid y nuestras matrices, empresas afiliadas y otras empresas
            con titularidad o control compartidos presentes y futuras;
          </p>
        </li>
        <li>
          <p>
            en la medida en que lo consideremos razonablemente apropiado, para
            evitar actividades maliciosas o fraudulentas, proteger los derechos,
            la privacidad, la seguridad o los bienes suyos, de los
            desarrolladores, de nuestros socios, de Plaid y de otros; o
          </p>
        </li>
        <li>
          <p>
            para cualquier otro fin notificado, con su consentimiento o según
            sus instrucciones.
          </p>
        </li>
      </ul>
      <p>
        (Para usuarios de los EE. UU.) No compartimos sus datos con terceros no
        afiliados, salvo en casos permitidos por ley (según se autoriza en el
        Código de Regulaciones Federales [Code of Federal Regulations, CFR],
        título 12, arts. 1016.14 y 1016.15).
      </p>
      <p>
        {' '}
        Cuando vincula sus cuentas financieras a través de Plaid, normalmente
        usamos el servicio reCAPTCHA de Google para ayudar a detectar fraudes y
        abusos. reCAPTCHA de Google está integrado en los productos y servicios
        de Plaid para garantizar que los datos ingresados en formularios en
        línea, por ejemplo, sean realmente realizados por personas reales y no
        de manera automatizada por software (o bots).{' '}
      </p>
      <p>
        reCAPTCHA de Google analiza el comportamiento de los usuarios de
        nuestros productos y servicios mediante diferentes características.
        reCAPTCHA de Google trata datos personales, como su dirección IP, el
        tiempo que permanece en nuestro sitio web y demás información sobre el
        uso que usted hace de los productos y servicios de Plaid. Google
        transmitirá y almacenará dichos datos en servidores que se encuentran en
        los Estados Unidos y en otros países. Google ha obtenido la
        certificación del Marco de Privacidad de Datos entre la UE y los EE. UU.
        (EU-US Data Privacy Framework); el certificado se encuentra disponible{' '}
        <Link href='https://www.dataprivacyframework.gov/s/participant-search/participant-detail?id=a2zt000000001L5AAI&status=Active'>
          <a>aquí</a>
        </Link>
        . Cuando usamos reCAPTCHA, se aplican la Política de privacidad y las
        Condiciones de uso de Google a reCAPTCHA y a la información que Google
        recaba mediante reCAPTCHA.
      </p>
      <p>
        Únicamente compartimos sus datos financieros personales con terceros
        para potenciar los servicios que solicitó, cuando usted brinda su
        consentimiento o a fin de protegerlo contra fraudes.
      </p>
      <p>
        Podemos recabar y compartir datos de cookies con terceros cuando usted
        visita nuestro sitio web, o podemos permitir que terceros recaben estos
        datos de cookies a partir de nuestros sitios. Le recomendamos que
        consulte la{' '}
        <Link href='#política-de-cookies'>
          <a>Política de cookies</a>
        </Link>{' '}
        y la{' '}
        <Link href='#declaración-de-privacidad'>
          <a>Política de privacidad para todos los públicos</a>
        </Link>{' '}
        si desea conocer más detalles.
      </p>

      <h5 id='nuestras-prácticas-de-conservación-y-eliminación'>
        Nuestras prácticas de conservación y eliminación
      </h5>
      <p>
        Conservamos sus datos solamente mientras sea necesario. Para determinar
        si los datos son necesarios, consideramos la razón por la cual se
        recabaron y utilizaron sus datos, y los requisitos legales para
        conservarlos. Revisamos sus datos de forma periódica para asegurarnos de
        que siguen siendo necesarios para cumplir con el fin para el que fueron
        recabados o para cualquier otro requisito legal. Si un desarrollador
        elimina la conexión que usted tiene desde la aplicación de este a sus
        datos, los sistemas de Plaid están diseñados para eliminar sus datos
        personales de forma automática, salvo determinadas excepciones en las
        que podremos conservar su información.
      </p>
      <p>
        Las excepciones pueden ser: (a) si usted estableció una conexión con la
        aplicación de otro desarrollador a través de Plaid que sigue activa; (b)
        si Plaid necesita sus datos para seguir brindándole un servicio de Plaid
        que usted solicitó; (c) si por ley Plaid debe conservar sus datos; (d)
        si Plaid necesita sus datos para ayudar a prevenir el fraude o proteger
        la privacidad, brindar soporte o investigar el uso o la conducta
        indebidos; (e) cuando Plaid haya anonimizado sus datos, de modo que no
        puedan volver a permitir la identificación; o (f) si le solicitamos que
        nos permita conservar sus datos por más tiempo y usted específicamente
        lo acepta.
      </p>
      <p>
        Sus datos únicamente serán tratados según lo exija la ley o de
        conformidad con esta Política.
      </p>
      <p>
        Le recomendamos que consulte la sección “Sus derechos en materia de
        protección de datos” de esta Política para conocer las opciones que
        puedan estar disponibles para usted, lo que incluye cómo solicitar la
        eliminación de sus datos. En la sección “Sus controles de su privacidad”
        de esta Política también se brindan detalles sobre las herramientas que
        tiene a su disposición, para ayudarlo a visualizar y administrar las
        conexiones que ha realizado a través de Plaid. También puede
        contactarnos en relación con nuestras prácticas de conservación de
        datos, usando la información de contacto en la sección “Cómo ponerse en
        contacto con Plaid”, más adelante.
      </p>

      <h5 id='protección-de-datos'>Protección de datos</h5>
      <p>
        Las políticas y prácticas de seguridad de Plaid están diseñadas para
        proteger la confidencialidad e integridad de sus datos. Plaid implementa
        controles creados para limitar el acceso a estos datos al personal que
        tiene un motivo comercial para conocerlos, y prohíbe al personal acceder
        a estos datos, divulgarlos o utilizarlos ilícitamente.
      </p>

      <h5 id='algunos-detalles-finales'>Algunos detalles finales</h5>

      <h5 id='transferencias-internacionales-de-datos'>
        Transferencias internacionales de datos
      </h5>
      <p>
        Operamos a nivel internacional y, por consiguiente, transferimos los
        datos que recabamos acerca de usted a través de fronteras
        internacionales para el tratamiento y almacenamiento (por ejemplo,
        transferimos datos desde el EEE y el RU a los Estados Unidos, y
        almacenamos datos en regiones de AWS ubicadas en los Estados Unidos). Al
        transferir datos a un país o territorio diferente, respetamos las leyes
        aplicables sobre protección de datos. En particular, cuando transferimos
        datos desde el EEE o el RU a través de otras fronteras internacionales,
        nos basamos en las decisiones de adecuación, acuerdos sobre
        transferencia de datos u otros mecanismos aprobados por la Comisión
        Europea o la Secretaría de Estado del Reino Unido (según corresponda)
        para dichas transferencias, incluidas las cláusulas contractuales tipo.
        Puede solicitar una copia de esas cláusulas contractuales tipo
        contactándonos según se indica debajo. Antes de transferir datos desde
        el EEE o el RU, realizamos evaluaciones del impacto de las
        transferencias e implementamos las medidas complementarias necesarias
        para garantizar que los datos transferidos sean conservados de
        conformidad con los requisitos impuestos por el EEE y el RU.
      </p>

      <h5 id='sus-derechos-en-materia-de-protección-de-datos'>
        Sus derechos en materia de protección de datos
      </h5>
      <p>
        Independientemente de su lugar de residencia, respetaremos los
        siguientes derechos relacionados con sus datos personales, sujeto a
        algunas limitaciones y excepciones establecidas por ley, y no será
        discriminado por ejercerlos:
      </p>
      <ul>
        <li>
          <p>acceder a los datos recabados sobre usted;</p>
        </li>
        <li>
          <p>
            solicitar el acceso a más detalles acerca de las categorías e
            información personal específica que podamos haber recabado acerca de
            usted en los últimos 12 meses (incluida la información personal
            comunicada para fines comerciales);
          </p>
        </li>
        <li>
          <p>
            solicitar, en determinadas circunstancias, que rectifiquemos o
            actualicemos sus datos porque son inexactos o están incompletos;
          </p>
        </li>
        <li>
          <p>
            solicitar, en determinadas circunstancias, que eliminemos sus datos
            o limitemos el tratamiento de sus datos;
          </p>
        </li>
        <li>
          <p>
            oponerse a nuestro tratamiento de sus datos en determinadas
            condiciones dispuestas por ley;
          </p>
        </li>
        <li>
          <p>
            retirar ese consentimiento cuando el tratamiento de sus datos se
            base en su consentimiento;
          </p>
        </li>
        <li>
          <p>
            solicitar que suministremos los datos recabados acerca de usted en
            un formato estructurado, de uso común y de lectura mecánica, para
            que usted pueda transferirlos a otra empresa, cuando sea
            técnicamente factible.
          </p>
          <ul>
            <li>
              <p>
                Recuerde que para obtener un registro oficial de su información
                financiera, debe realizar dicha solicitud directamente a su
                institución financiera.
              </p>
            </li>
          </ul>
        </li>
      </ul>

      <p>
        Según dónde viva, es posible que tenga derecho a presentar una queja.
        Agradecemos y apreciamos la oportunidad de abordar sus inquietudes, y lo
        alentamos a que se comunique con nosotros. Asimismo, y según sea su
        jurisdicción, podrá tener derecho a presentar una denuncia en cualquier
        momento ante la autoridad de control (de protección de datos). Para los
        usuarios finales en el EEE, puede encontrar la información de contacto
        en el{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_es'>
          <a>Comité Europeo de Protección de Datos (CEPD)</a>
        </Link>{' '}
        en el sitio web del CEPD. En el caso de los usuarios finales en el RU,
        se puede encontrar la información de contacto de la{' '}
        <Link href='https://ico.org.uk/'>
          <a>
            Oficina del Comisionado de Información (Information Commissioner’s
            Office, ICO)
          </a>
        </Link>{' '}
        en el sitio web del ICO. En el caso de los usuarios finales en Suiza,
        pueden encontrar la información del{' '}
        <Link href='https://www.edoeb.admin.ch/edoeb/en/home.html'>
          <a>Comisionado Federal de Protección de Datos e Información</a>
        </Link>
        .
      </p>

      <p>
        Para ejercer sus derechos, puede presentar una solicitud, a través de
        nuestro{' '}
        <Link href='https://my.plaid.com/data-subject-request-form-es'>
          <a>formulario en línea</a>
        </Link>{' '}
        . También puede contactarnos como se describe en la sección “Cómo
        ponerse en contacto con Plaid” más adelante, para ejercer cualquiera de
        sus derechos de protección de datos. Es posible que se le pida que
        proporcione información adicional necesaria para confirmar su identidad
        antes de que podamos responder a su solicitud.
      </p>
      <p>
        Si recibimos su solicitud de un agente autorizado, podremos solicitar
        constancia de que el agente tiene autoridad válida por escrito, como un
        poder notarial, para presentar solicitudes en nombre de usted.
      </p>
      <p>
        Consideraremos las solicitudes y proporcionaremos nuestra respuesta
        dentro de un periodo de tiempo razonable (y dentro de los plazos
        exigidos en virtud de la ley aplicable). No obstante, por favor tenga en
        cuenta que determinados datos podrían estar exentos de esas solicitudes,
        por ejemplo, si debemos conservarlos para cumplir con nuestras propias
        obligaciones legales o para interponer, ejercer o defender demandas
        legales.
      </p>
      <p>
        Tanto para Plaid Financial Ltd. como para Plaid, B.V. hemos designado a
        un delegado de protección de datos (Data Protection Officer, DPO) que
        también es responsable de supervisar las preguntas relacionadas con esta
        Política y con quien usted podrá comunicarse por correo electrónico a{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .{' '}
      </p>

      <h5 id='sus-controles-de-privacidad-adicionales'>
        Sus controles de privacidad adicionales
      </h5>
      <p>
        Plaid desarrollado{' '}
        <Link href='http://my.plaid.com'>
          <a>Portal Plaid</a>
        </Link>{' '}
        para brindarle una manera práctica y centralizada de visualizar y
        administrar las conexiones que usted haya hecho a través de Plaid.
      </p>
      <p>
        Si se encuentra en los EE. UU., puede registrarse en Plaid Portal. Para
        ello, visite my.plaid.com, verifique su número de teléfono y dirección
        de correo electrónico, y establezca una contraseña. Una vez que se haya
        registrado en Plaid Portal, será dirigido a un panel de control que
        podrá mostrar sus cuentas financieras conectadas a las aplicaciones que
        usted haya elegido al usar Plaid, y el tipo de datos que se comparten
        con esas aplicaciones. Además, Plaid Portal le proporciona controles
        para cancelar la conexión entre las aplicaciones y sus cuentas
        financieras, y eliminar los datos relacionados que se han almacenado en
        los sistemas de Plaid.
      </p>

      <h5 id='cambios-en-esta-política'>Cambios en esta Política</h5>
      <p>
        Actualizamos o cambiamos esta Política de forma periódica. Si efectuamos
        actualizaciones o cambios, publicaremos la nueva política en el sitio
        web de Plaid, en{' '}
        <Link href='/legal'>
          <a>https://plaid.com/legal</a>
        </Link>
        , y actualizaremos la fecha de entrada en vigencia en el encabezamiento
        de esta Política. También les notificaremos los cambios sustanciales a
        los desarrolladores, de conformidad con nuestros acuerdos de
        desarrolladores, ya que pueden estar en mejor posición para informarle
        los cambios en esta Política. Si usted es un usuario activo de Plaid
        Portal, también le enviaremos un mensaje de correo electrónico para
        informarle sobre la actualización.
      </p>

      <h5 id='cómo-ponerse-en-contacto-con-plaid'>
        Cómo ponerse en contacto con Plaid
      </h5>
      <p>
        Si usted tiene cualquier pregunta o queja sobre esta Política o sobre
        nuestras prácticas en materia de privacidad en general, puede
        contactarnos por correo electrónico a{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        , o por correo postal a:
      </p>
      <p>
        <em>Si reside fuera del EEE o del RU:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc.
        <br />
        A/A: Legal
        <br />
        PO Box 7775 #35278
        <br />
        San Francisco, California 94120-7775,
        <br />
        EE. UU
        <br />
        <br />
      </div>

      <p>
        Tanto para Plaid Financial Ltd. como para Plaid, B.V. hemos designado a
        un delegado de protección de datos (Data Protection Officer, DPO) que
        también es responsable de supervisar las preguntas relacionadas con esta
        Política y con quien usted podrá comunicarse por correo electrónico a{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        .
      </p>
      <br />
      <p>
        <br />
        <em>Si usted reside en el RU:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd.
        <br />
        A/A: Legal
        <br />
        New Penderel House, 4th Floor
        <br />
        283-288 High Holborn
        <br />
        Londres, Reino Unido, WC1V 7HP
      </div>
      <p>
        <br />
        <em>Si usted reside en el EEE:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid, B.V.
        <br />
        A/A: Legal
        <br />
        Muiderstraat 1
        <br />
        1011PZ Ámsterdam
        <br />
        Países Bajos
      </div>
      <br />
      <br />

      <h5 id='resúmenes-de-actividades-de-tratamiento'>
        Resúmenes de actividades de tratamiento
      </h5>

      <h5 id='resúmen-de-actividades-de-tratamiento-por-categoría-de-datos'>
        Resúmen de actividades de tratamiento por categoría de datos
      </h5>
      <p>
        Para ayudar a brindar aún mayor transparencia en torno a nuestras
        prácticas, hemos consolidado la información proporcionada en nuestra
        Política como se muestra anteriormente, en una tabla que concuerda con
        las categorías de los datos que Plaid recaba sobre usted con las fuentes
        de los datos, usos de los datos y categorías de receptores con los que
        Plaid comparte los datos. Para obtener descripciones más detalladas de
        nuestra recopilación, uso y prácticas de intercambio de información, le
        solicitamos que consulte las secciones anteriores de la Política.
      </p>

      <br />
      <br />
      <div className='eupp-container'>
        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Categoría de datos personales y ejemplos</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Fuente de los datos personales</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Usos de los datos personales </span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Categorías de destinatarios con las que se pueden compartir
                    los datos personales
                  </span>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Identificadores</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: nombre, direcci&oacute;n de correo
                    electr&oacute;nico, n&uacute;mero de tel&eacute;fono y
                    nombre de usuario.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Datos que usted proporciona.</span>
                  </li>
                  <li>
                    <span>Datos que recabamos de sus cuentas financieras.</span>
                  </li>
                  <li>
                    <span>Datos que recibimos desde sus dispositivos.</span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted desde los desarrolladores
                      de aplicaciones proporcionadas por Plaid.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos provenientes de nuestras empresas afiliadas para
                      prestarle servicios o soporte.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones de los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>Proveedores de servicios de pago. </span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de verificaci&oacute;n de
                      identidad.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                  <li>
                    <span>
                      Terceros, cuando lo requiera la ley, por ejemplo, a
                      fuerzas de seguridad u otras autoridades gubernamentales.
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Datos financieros</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: nombre y n&uacute;mero de cuentas financieras,
                    saldos e historial de transacciones.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Datos que usted proporciona.</span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted desde los desarrolladores
                      de aplicaciones proporcionadas por Plaid.
                    </span>
                  </li>
                  <li>
                    <span>Datos que recabamos de sus cuentas financieras.</span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted de otras fuentes.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que extraemos de la informaci&oacute;n que recabamos
                      de usted o de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>Proveedores de servicios de pago. </span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de verificaci&oacute;n de
                      identidad.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                  <li>
                    <span>
                      Terceros, cuando as&iacute; lo exija la ley, por ejemplo,
                      a fuerzas de seguridad u otras autoridades
                      gubernamentales.
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Datos comerciales</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: datos relativos a cu&aacute;les de nuestros
                    servicios utiliza a trav&eacute;s de las aplicaciones de los
                    desarrolladores, y las fechas y horas en las que hace uso de
                    ellos.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Datos que recabamos de sus cuentas financieras.</span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted desde los desarrolladores
                      de aplicaciones proporcionadas por Plaid.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos provenientes de nuestras empresas afiliadas para
                      prestarle servicios o soporte.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted de otras fuentes.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que extraemos de la informaci&oacute;n que recabamos
                      de usted o de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>Proveedores de servicios de pago. </span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de verificaci&oacute;n de
                      identidad.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                  <li>
                    <span>
                      Terceros, cuando as&iacute; lo exija la ley, por ejemplo,
                      a fuerzas de seguridad u otras autoridades
                      gubernamentales.
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Datos de ubicaci&oacute;n</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: configuraci&oacute;n de la zona horaria y
                    ubicaci&oacute;n del dispositivo.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Datos que recibimos desde sus dispositivos.</span>
                  </li>
                  <li>
                    <span>
                      Datos provenientes de nuestras empresas afiliadas para
                      prestarle servicios o soporte.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que extraemos de la informaci&oacute;n que recabamos
                      de usted o de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                  <li>
                    <span>
                      Terceros, cuando as&iacute; lo exija la ley, por ejemplo,
                      a fuerzas de seguridad u otras autoridades
                      gubernamentales.
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Datos profesionales</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: informaci&oacute;n sobre su empleador y sobre
                    la n&oacute;mina.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Datos que usted proporciona.</span>
                  </li>
                  <li>
                    <span>Datos que recabamos de sus cuentas financieras.</span>
                  </li>
                  <li>
                    <span>
                      Datos que recibimos sobre usted de otras fuentes.
                    </span>
                  </li>
                  <li>
                    <span>
                      Datos que extraemos de la informaci&oacute;n que recabamos
                      de usted o de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de verificaci&oacute;n de
                      identidad.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Datos sobre la actividad en las redes electr&oacute;nicas
                  </span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: el modelo de hardware y sistema operativo de su
                    dispositivo, y datos del navegador.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span></span>
                </p>
                <ul>
                  <li>
                    <span>Datos que recibimos desde sus dispositivos.</span>
                  </li>
                  <li>
                    <span>
                      Datos provenientes de nuestras empresas afiliadas para
                      prestarle servicios o soporte.
                    </span>
                  </li>
                </ul>
                <p>
                  <span></span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones.</span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Informaci&oacute;n que extraemos</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Por ejemplo: podemos extraer la ubicaci&oacute;n de la
                    direcci&oacute;n IP o sus ingresos anuales a partir de sus
                    recibos de sueldo.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Informaci&oacute;n que extraemos de los datos que
                      recabamos de usted o de otras fuentes.
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Prestar servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar servicios existentes.</span>
                  </li>
                  <li>
                    <span>
                      Ayudar a prevenir el fraude o proteger la privacidad y
                      seguridad.
                    </span>
                  </li>
                  <li>
                    <span>Desarrollar nuevos servicios.</span>
                  </li>
                  <li>
                    <span>Desarrollar conocimientos.</span>
                  </li>
                  <li>
                    <span>Brindar soporte.</span>
                  </li>
                  <li>
                    <span>Investigar usos indebidos y violaciones. </span>
                  </li>
                  <li>
                    <span>Con fines legales.</span>
                  </li>
                  <li>
                    <span>Con su consentimiento.</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Desarrolladores de&nbsp;aplicaciones que usted utiliza, y
                      seg&uacute;n las indicaciones del desarrollador.
                    </span>
                  </li>
                  <li>
                    <span>Instituciones financieras.</span>
                  </li>
                  <li>
                    <span>Proveedores de servicios de pago. </span>
                  </li>
                  <li>
                    <span>
                      Socios y contratistas en relaci&oacute;n con los servicios
                      que realizan para nosotros o los desarrolladores.
                    </span>
                  </li>
                  <li>
                    <span>Asesores profesionales.</span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios que ayudan a Plaid a analizar
                      datos para los fines comerciales de Plaid.{' '}
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de almacenamiento en la nube.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de desarrollo de software.
                    </span>
                  </li>
                  <li>
                    <span>
                      Proveedores de servicios de prevenci&oacute;n del fraude.
                    </span>
                  </li>
                  <li>
                    <span>Entidades del grupo Plaid.</span>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='resúmen-de-las-actividades-de-tratamiento-por-producto'>
        Resumen de las actividades de tratamiento por producto
      </h5>
      <p>
        Como se mencionó anteriormente, los datos que recabamos y usamos
        dependen de los productos y servicios de Plaid que usted y el
        desarrollador usan. Para facilitar la comprensión de qué datos se
        recaban y usan para cada producto y servicio, hemos elaborado los
        siguientes resúmenes sobre nuestra recopilación y uso de datos por
        producto. Algunos de estos productos únicamente están disponibles para
        los usuarios que se encuentran en los EE. UU. y otros solamente para
        usuarios que se encuentran en el EEE y el RU. Si no está seguro de
        cuáles productos o servicios de Plaid usan las aplicaciones a las que
        está conectado, le solicitamos que se comunique con el desarrollador de
        la aplicación. (Tenga en cuenta que esta lista refleja el conjunto de
        productos de Plaid a partir de la fecha de entrada en vigencia de esta
        Política y podría no incluir productos o servicios en desarrollo a
        partir de esa fecha).
      </p>
      <p>
        Además, la ley de protección de datos de algunas jurisdicciones, como el
        Reino Unido y la UE, diferencia entre el “responsable de controlar el
        tratamiento” y el “encargado del tratamiento” de los datos. En función
        del producto o servicio que usted y su desarrollador utilicen, Plaid
        puede ser el responsable de controlar el tratamiento o el encargado del
        tratamiento de los datos recabados en virtud de la ley de protección de
        datos pertinente. A menos que se especifique lo contrario en la
        descripción del producto a continuación, Plaid es el responsable de
        controlar el tratamiento de los datos personales recabados.{' '}
      </p>

      <h5>Assets (Activos)</h5>
      <p>
        Le permite brindar acceso a una visualización puntual de los datos de su
        cuenta financiera elegida (como saldos de cuenta, historial de
        transacciones e información sobre la identidad del titular de la cuenta)
        a Plaid y a las aplicaciones y servicios que usted elija. Recabamos
        identificadores, información financiera, datos comerciales, datos de
        ubicación, datos sobre la actividad en las redes electrónicas, y
        extraemos conclusiones a partir de estos datos. Utilizamos estos datos
        tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Auth (Autenticación)</h5>
      <p>
        Le permite autenticar los datos de su cuenta financiera y brindar acceso
        a su cuenta y números de ruta (routing number) a Plaid y a las
        aplicaciones y servicios que usted elija. Recabamos identificadores,
        datos financieros, datos comerciales, datos de ubicación y datos sobre
        la actividad en las redes electrónicas. Utilizamos esta información tal
        como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Balance (Saldo)</h5>
      <p>
        Balance y las funciones opcionales le permiten brindar acceso a los
        saldos de su cuenta financiera en tiempo real y extraer conclusiones a
        partir de los saldos de sus cuentas y demás información de las cuentas a
        Plaid, y a las aplicaciones y servicios que usted elija, para que puedan
        ayudarlo a tomar medidas, como evitar sobregiros en su cuenta antes de
        hacer una transferencia de dinero. Recabamos identificadores, datos
        financieros, datos comerciales, datos de ubicación y datos sobre la
        actividad en las redes electrónicas y, en algunos casos, extraemos
        conclusiones a partir de estos datos. Usamos estos datos tal como se
        describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Identity (Identidad) y Identity Match (Coincidencia de identidad)</h5>
      <p>
        La función de identidad le permite brindar acceso a los datos del
        titular de la cuenta que obran en poder de su entidad financiera (como
        su nombre, dirección de correo electrónico, número de teléfono y
        domicilio postal) a Plaid y a las aplicaciones y servicios que usted
        elija, para que estos puedan, por ejemplo, verificar su identidad o
        rellenar automáticamente los datos de su cuenta en la aplicación. La
        coincidencia de identidad ayuda a las aplicaciones y servicios que usted
        utiliza a verificar su identidad con los datos del titular de la cuenta
        que obran en poder de su institución financiera. Recabamos
        identificadores, datos financieros, datos comerciales, datos de
        ubicación y datos sobre la actividad en las redes electrónicas. Usamos
        estos datos tal como se describe anteriormente en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>
      <h5>Debit Card Verification (Verificación de tarjetas de débito)</h5>

      <p>
        La verificación de tarjetas de débito le permite proporcionar acceso a
        los datos del titular de la cuenta, al saldo de la cuenta financiera en
        tiempo real y a los datos del historial de transacciones, a fin de
        confirmar que la tarjeta de débito que está utilizando está vinculada a
        una cuenta bancaria de la que usted es titular. Recabamos
        identificadores, datos financieros, datos comerciales, datos de
        ubicación y datos sobre la actividad en las redes electrónicas y, en
        algunos casos, extraemos conclusiones a partir de estos datos. Usamos
        estos datos tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>
      <h5>
        Identity Verification and Monitor (Verificación y control de identidad)
      </h5>
      <p>
        Esta función (Identity Verification and Monitor, “IDV”) permite que los
        desarrolladores puedan llevar a cabo la verificación de identidad, la
        detección de fraudes y los servicios de verificación de listas de
        vigilancia. Cuando brindamos servicios de verificación y control de
        identidad a los desarrolladores, solamente recabamos y tratamos sus
        datos en calidad de proveedor de servicios para dicho desarrollador, por
        lo que son los desarrolladores quienes determinan el propósito y los
        medios por los cuales se tratan sus datos. Si desea conocer más detalles
        sobre cómo tratamos los datos del usuario final para los desarrolladores
        y, conforme a las instrucciones del desarrollador, para brindar
        servicios de verificación y control de identidad, consulte la{' '}
        <Link href='https://cognitohq.com/privacy-statement'>
          {' '}
          <a> Declaración de privacidad aplicable al usuario final</a>{' '}
        </Link>
        de nuestra empresa afiliada.
      </p>
      <h5>Plaid Account (Cuenta Plaid)</h5>
      <p>
        Le permite inscribirse en una experiencia de usuario recurrente más
        rápida al permitir que Plaid guarde las conexiones de su cuenta
        financiera y la información de identidad para una incorporación y uso
        más rápidos de las aplicaciones elegidas. Recabamos identificadores,
        datos financieros, datos comerciales, datos de ubicación y datos sobre
        la actividad en las redes electrónicas. Usamos estos datos tal como se
        describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>
      <h5>Income (Ingresos)</h5>
      <p>
        Le permite brindar acceso a sus datos relativos a sus ingresos y empleo
        a Plaid y a las aplicaciones y servicios que usted elija. Recabamos
        identificadores, datos financieros, datos comerciales, datos de
        ubicación, datos sobre empleo, ingresos y otros datos profesionales,
        datos sobre la actividad en las redes electrónicas, y extraemos
        conclusiones a partir de estos datos. Usamos estos datos tal como se
        describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Investment (Inversiones)</h5>
      <p>
        Le permite brindar acceso a los datos de sus cuentas de jubilación,
        corretaje, ahorros para educación y ahorros para la salud a Plaid y a
        las aplicaciones o servicios que usted elija, para que ellos puedan
        hacer cosas, como proporcionarle herramientas personales de gestión
        financiera y patrimonial. Recabamos identificadores, datos financieros,
        datos comerciales, datos de ubicación, datos profesionales, datos sobre
        la actividad en las redes electrónicas, y extraemos conclusiones a
        partir de estos datos. Usamos estos datos tal como se describe en la
        sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Liabilities (Pasivos)</h5>
      <p>
        Le permite brindar acceso a los datos financieros de sus cuentas de
        tarjetas de crédito, hipotecas y préstamos estudiantiles a Plaid y a las
        aplicaciones y servicios que usted elija. Recabamos identificadores,
        datos financieros, datos comerciales, datos de ubicación, datos sobre la
        actividad en las redes electrónicas, y extraemos conclusiones a partir
        de estos datos. Usamos estos datos tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>
        Payment Initiation (Iniciación de pagos) y Variable Recurring Payments
        (Pagos periódicos variables)
      </h5>
      <p>
        La iniciación de pagos les permite a los usuarios de los servicios en la
        UE y el RU efectuar pagos en sus cuentas por única vez, pagos periódicos
        o periódicos variables. Es para servicios como transferencias bancarias,
        órdenes permanentes, pagos periódicos variables, y pagos por única vez o
        periódicos de las cuentas, como retiros y reembolsos. Los pagos
        periódicos variables permiten que nuestros usuarios de la UE y el RU
        autentiquen una cuenta bancaria una vez, y luego puedan efectuar pagos
        futuros sin necesidad de autenticación adicional. Recabamos
        identificadores, datos financieros, datos comerciales, datos de
        ubicación, datos sobre la actividad en las redes electrónicas, y
        extraemos conclusiones a partir de estos datos. Usamos estos datos tal
        como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Payouts (Pagos)</h5>
      <p>
        Les permite a los usuarios de los servicios en la UE y el RU efectuar
        retiros y reembolsos de manera fluida e instantánea desde una aplicación
        o servicio que esté usando, mediante la apertura y prefinanciación de
        una cuenta de dinero electrónico desde la que se pueden enviar fondos a
        los usuarios que soliciten un pago. Recabamos identificadores, datos
        financieros, datos comerciales, datos de ubicación, datos sobre la
        actividad en las redes electrónicas, y extraemos conclusiones a partir
        de estos datos. Usamos estos datos tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Plaid Portal</h5>
      <p>
        Plaid Portal es una herramienta de privacidad que usted puede usar como
        método para controlar las conexiones con las aplicaciones que ha
        realizado a través de Plaid. Puede usar Plaid Portal para visualizar las
        conexiones con las aplicaciones en un único panel de control,
        desconectar las cuentas financieras de las aplicaciones o eliminar datos
        de los sistemas de Plaid. También puede usar Plaid Portal para ver y
        administrar sus cuentas de Plaid. Recabamos identificadores, datos
        financieros, datos comerciales, datos de ubicación y datos sobre la
        actividad en las redes electrónicas. Usamos estos datos tal como se
        describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Signal (Señal)</h5>
      <p>
        En el caso de los usuarios de los EE. UU., permite que las aplicaciones
        y los servicios que utilizan les proporcionen una mejor experiencia de
        usuario, como brindarles un acceso más rápido a sus fondos y reducir el
        riesgo de fraude o de que un pago no se complete. Recabamos
        identificadores, datos financieros, datos comerciales, datos de
        ubicación, datos sobre la actividad en las redes electrónicas, y
        extraemos conclusiones a partir de estos datos. Usamos estos datos tal
        como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Statements (Resúmenes de cuenta)</h5>
      <p>
        Le permite compartir copias de sus resúmenes de cuentas financieras con
        Plaid y las aplicaciones y servicios que elija. Podemos recabar
        identificadores, datos financieros, datos comerciales, datos de
        ubicación y datos sobre la actividad en las redes electrónicas.
        Utilizamos estos datos tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Transactions (Transacciones)</h5>
      <p>
        Las transacciones y los productos relacionados le permiten brindar
        acceso al historial de transacciones desde sus cuentas financieras a
        Plaid y a las aplicaciones y servicios que usted elija, para que le
        puedan proporcionar, por ejemplo, herramientas personales de gestión
        financiera, informes de gastos y modelos de flujos de caja, entre otras
        cosas. Podemos recabar identificadores, datos financieros, datos
        comerciales, datos de ubicación, datos sobre la actividad en las redes
        electrónicas, y extraemos conclusiones y conocimientos financieros a
        partir de estos datos. Usamos estos datos tal como se describe en la
        sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <h5>Transfer (Transferencia)</h5>
      <p>
        Le permite autorizar a un desarrollador a enviarle pagos a usted o
        cobrarle pagos, mediante transferencias bancarias, ya sea por única vez
        o de manera periódica. Esto incluye verificar que usted sea el titular
        de la cuenta a la cual o desde la cual se enviará el pago, evaluar el
        riesgo de que el pago no pueda completarse o sea fraudulento, procesar
        electrónicamente las instrucciones de pago y confirmar al desarrollador
        que el pago se ha realizado. Podemos recabar identificadores, datos
        financieros, datos de ubicación y datos sobre la actividad en las redes
        electrónicas. Usamos estos datos tal como se describe en la sección{' '}
        <Link href='#como-usamos-sus-datos'>
          <a>Cómo usamos sus datos</a>
        </Link>{' '}
        de esta Política.
      </p>

      <br />
      <hr className='section-divider' />

      <h5 id='aviso-de-privacidad-para-el-consumidor'>
        Aviso de privacidad para el consumidor
      </h5>
      <p>
        <span>Última actualización: </span>
        <span> 22 de febrero de 2024</span>
      </p>

      <table className='eupp-table'>
        <thead>
          <tr>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span></span>
              </p>
            </td>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿QUÉ HACE PLAID CON SU INFORMACIÓN PERSONAL?</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Por qué?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Las empresas financieras eligen cómo comparten su información
                  personal. Le legislación federal les otorga a los consumidores
                  el derecho a limitar parte del intercambio de información,
                  pero no la totalidad. Además, la legislación federal nos exige
                  que le informemos cómo recabamos, compartimos y protegemos su
                  información personal. Le solicitamos que lea este aviso
                  detenidamente para comprender lo que hacemos.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Qué?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Los tipos de información personal que recabamos y compartimos
                  dependen del producto o servicio que usted use o que use el
                  desarrollador de la aplicación a la que se conecta. Esta
                  información puede incluir:
                </span>
              </p>
              <ul>
                <li>
                  <span>Número de seguro social y saldos de la cuenta.</span>
                </li>
                <li>
                  <span>
                    Transacciones de la cuenta e historial de transacciones.
                  </span>
                </li>
                <li>
                  <span>Información sobre ingresos y empleo.</span>
                </li>
              </ul>
              <p>
                <span>
                  Para conocer más sobre los tipos de información personal que
                  recabamos, le solicitamos que consulte la sección denominada{' '}
                  <Link href='#datos-que-recabamos-y-categorías-de-fuentes'>
                    <a data-smooth-scroll data-offset='80'>
                      Datos que recabamos o extraemos
                    </a>
                  </Link>{' '}
                  , de nuestra Política de privacidad aplicable al usuario
                  final.
                </span>
              </p>
              <p>
                <span>
                  Cuando usted ya no sea nuestro consumidor, seguiremos usando y
                  compartiendo su información según se describe en este aviso.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Cómo?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Todas las empresas financieras deben compartir información
                  personal de los consumidores para proporcionar sus productos y
                  servicios. En la siguiente sección, indicamos las razones por
                  las cuales las empresas financieras pueden compartir la
                  información personal de sus consumidores; las razones por las
                  que Plaid decide compartirla y si usted puede limitar dicho
                  intercambio de información.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Razones por las cuales podemos compartir su información
                  personal
                </strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Plaid comparte información?</strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  ¿Usted puede limitar este intercambio de información?
                </strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Para nuestros fines comerciales cotidianos: </strong>

                <span>
                  como conectarse y compartir datos desde sus cuentas
                  financieras para accionar las aplicaciones que usted usa, como
                  también protegerlo contra el fraude o el uso indebido,
                  responder a órdenes judiciales e investigaciones legales, o
                  brindar informes a las agencias de crédito.
                </span>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Sí</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Para fines de marketing. </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No compartimos información. </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Para fines de marketing conjunto con otras empresas
                  financieras.
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No compartimos información. </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Para los fines comerciales cotidianos de nuestras empresas
                  afiliadas: información sobre transacciones y experiencias.
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Sí</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Sí, y únicamente compartimos información según sus
                  indicaciones.{' '}
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Para los fines comerciales cotidianos de nuestras empresas
                  afiliadas: información sobre solvencia crediticia.
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No compartimos información. </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Para que las empresas no afiliadas le ofrezcan sus productos.
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>No compartimos información. </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Preguntas</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Escríbanos a{' '}
                  <Link href='mailto:privacy@plaid.com'>
                    <a>privacy@plaid.com</a>
                  </Link>{' '}
                  .
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Quiénes somos</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Quién entrega este aviso?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Plaid Inc.</span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Qué hacemos</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Cómo protege Plaid mi información personal?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Para proteger su información personal del acceso y uso no
                  autorizados, empleamos medidas de seguridad que cumplen con la
                  legislación federal. Estas medidas incluyen protecciones
                  informáticas, y archivos y edificios seguros.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>¿Cómo recaba Plaid mi información personal?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Como se explica más detalladamente en la sección denominada{' '}
                  <Link href='#datos-que-recabamos-y-categorías-de-fuentes'>
                    <a data-smooth-scroll data-offset='80'>
                      Datos que recabamos o extraemos
                    </a>
                  </Link>{' '}
                  , de nuestra Política de privacidad aplicable al usuario
                  final, recabamos la información personal que usted nos
                  proporciona y también la que proviene de otras empresas cuando
                  usted usa nuestros productos o servicios. Por ejemplo, esto
                  incluye cuando usted usa Plaid Portal o cuando utiliza Plaid
                  para conectarse y compartir sus datos para accionar las
                  aplicaciones (“aplicaciones”) que utiliza.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  ¿Por qué no puedo limitar todo el intercambio de información?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>La legislación federal le otorga solo el derecho:</span>
              </p>
              <ul>
                <li>
                  <span>
                    a limitar el intercambio de información para los fines
                    comerciales cotidianos de las empresas afiliadas:
                    información sobre su solvencia crediticia;
                  </span>
                </li>
                <li>
                  <span>
                    a impedir que las empresas afiliadas usen su información
                    para ofrecerle productos;
                  </span>
                </li>
                <li>
                  <span>
                    a limitar el intercambio de información para que las
                    empresas no afiliadas le ofrezcan sus productos.
                  </span>
                </li>
              </ul>
              <p>
                <span></span>
              </p>
              <p>
                <span>
                  Las leyes estatales y las empresas individuales pueden
                  otorgarle derechos adicionales para limitar el intercambio de
                  información. Consulte más adelante para obtener más
                  información sobre sus derechos en virtud de la legislación
                  estatal.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Definiciones</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Empresas afiliadas</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Empresas con titularidad o control compartidos. Pueden ser
                  empresas financieras o no financieras.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    Entre nuestras empresas afiliadas se incluyen Plaid
                    Financial Ltd., Plaid, B. V. y Plaid Consumer Reporting
                    Agency Inc. (“Plaid CRA”).
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Empresas no afiliadas</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Empresas que no tienen titularidad o control compartidos.
                  Pueden ser empresas financieras o no financieras.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    Plaid no comparte información con empresas no afiliadas para
                    que puedan ofrecerle productos.
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Marketing conjunto</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Es un acuerdo formal entre empresas financieras no afiliadas
                  que juntas le ofrecen productos o servicios financieros con
                  usted.
                </span>
              </p>
              <ul>
                <li>
                  <span>Plaid no realiza marketing conjunto.</span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>Otra información importante </strong>
              </p>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>California: </strong>
                <span>
                  Si usted reside en California, no compartiremos la información
                  personal que recabemos acerca de usted, salvo en la medida
                  permitida por la legislación de California.
                </span>
              </p>
              <p>
                <span></span>
              </p>
              <p>
                <strong>Vermont: </strong>
                <span>
                  Si usted reside en Vermont, no compartiremos la información
                  personal que recabemos acerca de usted con empresas no
                  afiliadas, a menos que la legislación lo permita o usted lo
                  autorice.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <hr className='section-divider' />
    </>
  );
};

EsVersion.propTypes = propTypes;
export default EsVersion;
